import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import {
  Search,
  Calendar,
  CalendarDays,
  Maximize2,
  Minimize2,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import comercioService from "../actividadEconomica/service/comercio.service";
import { Dimmer } from "../../spinner";
import { DeclararUC } from "./modalDeclararUC";
import { DeclararUCRet } from "./modalDeclararUCRet";
import { ActualizarCamposComercio } from "./modalActualizarCamposComercio";
import { Mensaje } from "../../mensajes2";
import { Toasts } from "../../toasts";
import { formatMoneda } from "../../../util/varios";
import ModalImprimirContriDecla from "./modalImprimirContriDecla";
import { ActualizarEstadoCuenta } from "./modalActualizarEstadoCuenta";
import Axios from "axios";
import ModalDetalladoContribuyentes from "./ModalDetalladoContribuyentes";
import moment from "moment";
// Función para transformar los datos del backend al formato que espera el componente
const transformBackendData = (backendData, espiritu) => {
  if (!backendData || backendData.length === 0) {
    return {
      declaracionesPorDia: [],
      topDias: [],
      topContribuyentes: [],
      todosContribuyentes: [],
      distributionData: [],
      resumen: {
        total_declaraciones: 0,
        total_contribuyentes: 0,
        gran_total_declarado: 0,
        gran_total_petro: 0,
        promedio_por_declaracion: 0,
      },
      mostrarQuincenas: espiritu == 0,
    };
  }

  const mostrarQuincenas = espiritu == 0;
  const porDia = new Map();
  const porContribuyente = new Map();

  backendData.forEach((record) => {
    const fecha = new Date(record.fechadeclaracion);
    const fechaStr = fecha.toISOString().split("T")[0];
    const dia = record.diadecla || fecha.getDate();
    // Si no muestra quincenas, usar un valor fijo
    const quincena = mostrarQuincenas ? record.qcna : "Pago Mensual";
    const montoDeclarado = parseFloat(record.montodecla);
    const montoPetro = parseFloat(record.montopagar);
    const montoPagardolar = parseFloat(record.monto_pagar_dolar);
    const montoPagarBs = parseFloat(record.monto_pagar_bs);
    // Agrupar por día (y quincena si aplica)
    const key = mostrarQuincenas ? `${fechaStr}|${quincena}` : fechaStr;
    if (!porDia.has(key)) {
      porDia.set(key, {
        fecha: fechaStr,
        dia: dia,
        quincena: quincena,
        cantidad_declaraciones: 0,
        monto_total_dia: 0,
      });
    }
    const diaData = porDia.get(key);
    diaData.cantidad_declaraciones++;
    diaData.monto_total_dia += montoDeclarado;

    // Agrupar por contribuyente
    const contribuyenteId = record.idcomercio;
    if (!porContribuyente.has(contribuyenteId)) {
      porContribuyente.set(contribuyenteId, {
        idcomercio: record.idcomercio,
        rif: record.rif?.trim(),
        nombre: record.nombre?.trim(),
        reprelegal: record.reprelegal?.trim(),
        cantidad_declaraciones: 0,
        total_declarado: 0,
        total_petro: 0,
        montoPagardolar: 0,
        montoPagarBs: 0,
      });
    }
    const contribuyenteData = porContribuyente.get(contribuyenteId);
    contribuyenteData.cantidad_declaraciones++;
    contribuyenteData.total_declarado += montoDeclarado;
    contribuyenteData.total_petro += montoPetro;
    contribuyenteData.montoPagardolar += parseFloat(montoPagardolar);
    contribuyenteData.montoPagarBs += parseFloat(montoPagarBs);
  });

  const declaracionesPorDia = Array.from(porDia.values()).sort((a, b) =>
    a.fecha.localeCompare(b.fecha),
  );

  const topDias = [...declaracionesPorDia]
    .sort((a, b) => b.cantidad_declaraciones - a.cantidad_declaraciones)
    .slice(0, 10);

  const todosContribuyentes = Array.from(porContribuyente.values()).map(
    (c) => ({
      ...c,
      promedio_declarado: c.total_declarado / c.cantidad_declaraciones,
    }),
  );

  const topContribuyentes = [...todosContribuyentes]
    .sort((a, b) => b.total_declarado - a.total_declarado)
    .slice(0, 10);

  // Distribución - solo si muestra quincenas
  let distributionData = [];
  if (mostrarQuincenas) {
    const primeraQuincena = declaracionesPorDia
      .filter((d) => d.quincena === "1ra Quincena")
      .reduce((sum, d) => sum + d.cantidad_declaraciones, 0);

    const segundaQuincena = declaracionesPorDia
      .filter((d) => d.quincena === "2da Quincena")
      .reduce((sum, d) => sum + d.cantidad_declaraciones, 0);

    distributionData = [
      { name: "1ra Quincena", value: primeraQuincena, color: "#3B82F6" },
      { name: "2da Quincena", value: segundaQuincena, color: "#10B981" },
    ];
  }

  // Resumen
  const totalDeclaraciones = backendData.length;
  const totalContribuyentes = porContribuyente.size;
  const granTotalDeclarado = backendData.reduce(
    (sum, r) => sum + parseFloat(r.montodecla),
    0,
  );
  const granTotalPetro = backendData.reduce(
    (sum, r) => sum + parseFloat(r.montopagar),
    0,
  );
  const totalAPagar = backendData.reduce(
    (sum, r) => sum + parseFloat(r.monto_pagar_bs),
    0,
  );

  const resumen = {
    total_declaraciones: totalDeclaraciones,
    total_contribuyentes: totalContribuyentes,
    gran_total_declarado: granTotalDeclarado,
    gran_total_petro: granTotalPetro,
    promedio_por_declaracion: totalAPagar,
  };

  return {
    declaracionesPorDia,
    topDias,
    topContribuyentes,
    todosContribuyentes,
    distributionData,
    resumen,
    mostrarQuincenas,
  };
};

const AnalisisModuloAE = (props) => {
  const op = require("../../../../src/modulos/datos");
  const token = localStorage.getItem("jwtToken");
  const fecha = new Date();
  const numeroMes = fecha.getMonth() + 1;
  const anioActual = fecha.getFullYear();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [todosContri, setTodosContri] = useState(false);
  const [mostrarContriDecla, setMostrarContriDecla] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activate, setActivate] = useState(false);
  const [mostrar5, setMostrar5] = useState(false);
  const [mostrar6, setMostrar6] = useState(false);
  const [mostrar2, setMostrar2] = useState(false);
  const [comercio, setComercio] = useState();
  const [persona, setPersona] = useState();
  const [dataComercios, setDataComercios] = useState([]);
  const [dataGraficaPeriodo, setGraficaPeriodo] = useState([]);
  const [actividad, setActividad] = useState();
  const [configCome, setConfigCome] = useState();
  const [actividadxCome, setActividadxCome] = useState();
  const [deuda, setDeuda] = useState();
  const [historico, setHistorico] = useState();
  const [factorCambio, setFactorCambio] = useState(0);
  const [totDeuda, setTotDeuda] = useState("0,00");
  const [operacion, setOperacion] = useState(0);
  const [mostrarDetallado, setMostrarDetallado] = useState(false);
  const [idUsuario, setIdUsuario] = useState();
  const [deshabilitado, setDeshabilitado] = useState(false);
  const [mensaje, setMensaje] = useState({
    mostrar: false,
    titulo: "",
    texto: "",
    icono: "",
  });
  const [toast, setToast] = useState({
    mostrar: false,
    mensaje: "",
    titulo: "",
  });
  const [topDiasLimit, setTopDiasLimit] = useState(10);
  const [topContribuyentesLimit, setTopContribuyentesLimit] = useState(10);
  const [expandedChart, setExpandedChart] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [filtroTipo, setFiltroTipo] = useState("periodo");
  const [mesPeriodo, setMesPeriodo] = useState(numeroMes);
  const [anioPeriodo, setAnioPeriodo] = useState("2026");
  const [fechaDesde, setFechaDesde] = useState(`${anioActual}-01-01`);
  const [fechaHasta, setFechaHasta] = useState(
    fecha.toISOString().split("T")[0],
  );

  // ========== VARIABLES DE LOCALSTORAGE ==========
  const espiritu = parseInt(localStorage.getItem("espiritu"));
  const fechaSistema = localStorage.getItem("fechasistema");
  const anioActu = localStorage.getItem("anioactu");
  const [chartType, setChartType] = useState(
    espiritu === 0 ? "stacked" : "bar",
  );
  const [declaracionesData, setDeclaracionesData] = useState({
    declaracionesPorDia: [],
    topDias: [],
    topContribuyentes: [],
    todosContribuyentes: [],
    distributionData: [],
    resumen: {
      total_declaraciones: 0,
      total_contribuyentes: 0,
      gran_total_declarado: 0,
      gran_total_petro: 0,
      promedio_por_declaracion: 0,
    },
    mostrarQuincenas: espiritu == 0,
  });

  const COLORS = {
    primary: "#3B82F6",
    success: "#10B981",
    warning: "#F59E0B",
    danger: "#EF4444",
    purple: "#8B5CF6",
    info: "#06B6D4",
    quincena1: "#3B82F6",
    quincena2: "#10B981",
  };

  const GRADIENTS = {
    blue: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
    green: "linear-gradient(135deg, #0ba360 0%, #3cba92 100%)",
    red: "linear-gradient(135deg, #ff512f 0%, #dd2476 100%)",
    purple: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    dark: "linear-gradient(135deg, #0f2027 0%, #203a43 100%)",
  };

  const MESES = [
    { value: "1", label: "ENERO" },
    { value: "2", label: "FEBRERO" },
    { value: "3", label: "MARZO" },
    { value: "4", label: "ABRIL" },
    { value: "5", label: "MAYO" },
    { value: "6", label: "JUNIO" },
    { value: "7", label: "JULIO" },
    { value: "8", label: "AGOSTO" },
    { value: "9", label: "SEPTIEMBRE" },
    { value: "10", label: "OCTUBRE" },
    { value: "11", label: "NOVIEMBRE" },
    { value: "12", label: "DICIEMBRE" },
  ];

  const ANIOS = [
    { value: "2026", label: "2026" },
    { value: "2025", label: "2025" },
    { value: "2024", label: "2024" },
    { value: "2023", label: "2023" },
    { value: "2022", label: "2022" },
  ];

  // ========== FUNCIONES DE UTILERÍA ==========
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "Bs. 0,00";
    return new Intl.NumberFormat("es-VE", {
      style: "currency",
      currency: "VES",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "0";
    return new Intl.NumberFormat("es-VE").format(value);
  };

  const getTopDias = () => {
    if (!declaracionesData.declaracionesPorDia?.length) return [];
    const sorted = [...declaracionesData.declaracionesPorDia].sort(
      (a, b) => b.cantidad_declaraciones - a.cantidad_declaraciones,
    );
    return sorted.slice(0, topDiasLimit);
  };

  const getSubtituloFiltro = () => {
    if (filtroTipo === "periodo") {
      const mesLabel = MESES.find((m) => m.value === mesPeriodo)?.label || "";
      const tipoTexto = espiritu == 0 ? "Con Quincenas" : "Pago Mensual";
      return `Resumen de declaraciones — ${mesLabel} ${anioPeriodo} | ${tipoTexto}`;
    }
    if (fechaDesde && fechaHasta) {
      const tipoTexto = espiritu == 0 ? "Con Quincenas" : "Pago Mensual";
      return `Resumen de declaraciones — Del ${fechaDesde} al ${fechaHasta} | ${tipoTexto}`;
    }
    return "Resumen de declaraciones";
  };

  const getTituloGrafico = () => {
    if (filtroTipo === "periodo") {
      const mesLabel = MESES.find((m) => m.value === mesPeriodo)?.label || "";
      return `Actividad por Día — ${mesLabel} ${anioPeriodo}`;
    }
    if (fechaDesde && fechaHasta) {
      return `Actividad por Día — ${fechaDesde} al ${fechaHasta}`;
    }
    return "Actividad por Día";
  };

  // ========== FUNCIONES DE CONFIGURACIÓN ==========
  const seleccionarConfiguracion = async () => {
    try {
      const data = await comercioService.getConfiguracionIndustria();
      setConfigCome(data);
    } catch (error) {
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
    }
  };

  const seleccionarFactorCambio = async () => {
    try {
      const data = await comercioService.getFactorCambio();
      setFactorCambio(data.valor);
    } catch (error) {
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
    }
  };
  // ========== FUNCIONES PARA DECLARACIÓN ==========
  const SelectDeudaC = async (idcomercio) => {
    try {
      let data;
      if (espiritu === 100) {
        data = await comercioService.getDeudaComercioRS(
          idcomercio,
          fechaSistema,
          espiritu,
          1,
        );
      } else {
        data = await comercioService.getDeudaComercio(
          idcomercio,
          fechaSistema,
          espiritu,
          1,
        );
      }

      var mesactu = (new Date(fechaSistema).getMonth() + 1)
        .toString()
        .padStart(2, 0);
      var deu = [];
      var his = [];
      var totdeu = parseFloat(0.0);

      for (let i = 0; i < data.length; i++) {
        if (data[i].fechapago === null) {
          if (data[i].nombredeuda === "deudacomercio") {
            totdeu =
              totdeu +
              parseFloat(data[i].mondeu) +
              parseFloat(data[i].intmoradeu) +
              parseFloat(data[i].recargodeu) +
              parseFloat(data[i].multa);
            deu.push({
              anio: data[i].anio,
              mes: data[i].mes.replace("'", "").replace("'", ""),
              monto: data[i].mondeu,
              intereses: data[i].intmoradeu,
              recargo: data[i].recargodeu,
              multa: data[i].multa,
              retenciones: data[i].retencion,
              credito: data[i].acreditacion,
              totalperi: data[i].subtotal,
              status: data[i].estatus,
              idfac: data[i].idfact,
              nummes: data[i].nmes,
              estatusfac: data[i].estatusfac,
              tipotransaccion: data[i].tipotransaccion,
            });
          } else if (
            parseInt(data[i].anio) < parseInt(anioActu) ||
            (parseInt(data[i].anio) === parseInt(anioActu) &&
              parseInt(data[i].nmes) < parseInt(mesactu))
          ) {
            totdeu =
              totdeu +
              parseFloat(data[i].mondeu) +
              parseFloat(data[i].intmoradeu) +
              parseFloat(data[i].recargodeu) +
              parseFloat(data[i].multa);
            deu.push({
              anio: data[i].anio,
              mes: data[i].mes.replace("'", "").replace("'", ""),
              monto: data[i].mondeu,
              intereses: data[i].intmoradeu,
              recargo: data[i].recargodeu,
              multa: data[i].multa,
              retenciones: data[i].retencion,
              credito: data[i].acreditacion,
              totalperi: data[i].subtotal,
              status: data[i].estatus,
              idfac: data[i].idfact,
              nummes: data[i].nmes,
              estatusfac: data[i].estatusfac,
              tipotransaccion: data[i].tipotransaccion,
            });
          }
        } else {
          his.push({
            anio: data[i].anio,
            mes: data[i].mes.replace("'", "").replace("'", ""),
            monto: data[i].mondeu,
            intereses: data[i].intmoradeu,
            recargo: data[i].recargodeu,
            multa: data[i].multa,
            descuento: data[i].mondescue,
            recibo: data[i].numreci,
            fechapago: data[i].fechapago,
            status: data[i].estatus,
            idfac: data[i].idfact,
            estatusfac: data[i].estatusfac,
            tipotransaccion: data[i].tipotransaccion,
          });
        }
      }
      setTotDeuda(
        formatMoneda(totdeu.toString().replace(".", ","), ",", ".", 2),
      );
      setDeuda(deu);
      setHistorico(his);
      setActivate(false);

      if (espiritu === 100) {
        setMostrar6(true);
      } else {
        setMostrar5(true);
      }
    } catch (error) {
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
      setActivate(false);
    }
  };

  const SelectActividadComercio = async (idcomercio, anio) => {
    try {
      const data = await comercioService.getActividadxComercio(
        idcomercio,
        anio,
      );
      if (data !== 0 && data.length > 0) {
        var axc = [];
        for (let i = 0; i < data.length; i++) {
          axc.push({
            alicuota: data[i].alicuota,
            codigoactividad: data[i].codigoactividad,
            codigociu: data[i].codigociu,
            descripcion: data[i].descripcion,
            idactividad: data[i].idactividad,
            minimo: data[i].minimo,
          });
        }
        setActividadxCome(axc);
      } else {
        setActividadxCome("");
      }
      await SelectDeudaC(idcomercio);
    } catch (error) {
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
      setActivate(false);
    }
  };

  const SelectActividad = async (idcomercio, idactividad) => {
    try {
      if (idactividad > 0) {
        await comercioService.getActividad(idactividad);
      }
      const anioEfiscal = parseInt(
        JSON.parse(localStorage.getItem("efiscalanio")),
      );
      await SelectActividadComercio(idcomercio, anioEfiscal);
    } catch (error) {
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
      setActivate(false);
    }
  };
  const SelectComercio = async (idcomercio) => {
    setActivate(true);
    try {
      const comercioData = await comercioService.getComercioPersona(idcomercio);
      if (comercioData.idcomercio > 0) {
        setComercio(comercioData);
        setPersona({
          cedula: comercioData.cedula,
          nombres: comercioData.nombres,
          apellidos: comercioData.apellidos,
          correoe: comercioData.correoe,
          tlf1: comercioData.tlf1,
          tlf2: comercioData.tlf2,
          fnacimiento: comercioData.fnacimiento,
          nacionalidad: comercioData.nacionalidad,
          sexo: comercioData.sexo,
          direccion: comercioData.direccionp,
        });
        let idactividad =
          comercioData.idactividad === null ? 0 : comercioData.idactividad;
        await SelectActividad(idcomercio, idactividad);
      } else {
        setMensaje({
          mostrar: true,
          titulo: "ERROR",
          texto: "error al cargar el comercio",
          icono: "error",
        });
        setActivate(false);
      }
    } catch (error) {
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
      setActivate(false);
    }
  };

  const realizarDeclaracion = (id) => async (event) => {
    event.preventDefault();
    setActivate(true);
    setOperacion(1);
    await SelectComercio(id);
  };

  const cerrarModalActualizar = () => {
    setComercio();
    setActividadxCome();
    setPersona();
    if (espiritu === 100) {
      setMostrar6(false);
    } else {
      setMostrar5(false);
    }
  };

  const validarActuComercio = () => {
    setComercio();
    setPersona();
    if (espiritu === 100) {
      setMostrar6(false);
    } else {
      setMostrar5(false);
    }
  };

  const validarActuCamposComercio = (actu) => {
    setComercio();
    setPersona();
    setMostrar2(false);
    if (!actu) {
      setActivate(false);
      setMensaje({
        mostrar: true,
        titulo: "Información",
        texto: "Debe actualizar el correo para poder declarar.",
        icono: "informacion",
      });
    }
  };

  const cerrarToast = () => {
    setToast({
      ...toast,
      mostrar: false,
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let rawData;
      if (filtroTipo === "periodo") {
        rawData = await comercioService.resumenDeclaracion(
          "",
          "",
          "",
          mesPeriodo,
          anioPeriodo,
          espiritu,
          "periodo",
        );
      } else {
        if (!fechaDesde || !fechaHasta) {
          setLoading(false);
          return;
        }
        rawData = await comercioService.resumenDeclaracion(
          "",
          fechaDesde,
          fechaHasta,
          "",
          "",
          espiritu,
          "fecha",
        );
      }
      const transformedData = transformBackendData(rawData, espiritu);
      setDeclaracionesData(transformedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const buscarComerciosDeclarados = async () => {
    try {
      const campos = `
      m.nummes AS nummes,
      m.mes AS mes,
      COUNT(DISTINCT CASE WHEN d.idcomercio IS NOT NULL THEN c.idcomercio END) AS declararon,
      COUNT(DISTINCT CASE WHEN d.idcomercio IS NULL     THEN c.idcomercio END) AS faltaron
    `;
      const tabla = `
       (VALUES 
        (1,'ENERO'),(2,'FEBRERO'),(3,'MARZO'),(4,'ABRIL'),
        (5,'MAYO'),(6,'JUNIO'),(7,'JULIO'),(8,'AGOSTO'),
        (9,'SEPTIEMBRE'),(10,'OCTUBRE'),(11,'NOVIEMBRE'),(12,'DICIEMBRE')
      ) AS m(nummes, mes)
      CROSS JOIN comercio c
      LEFT JOIN djurada d 
        ON d.idcomercio = c.idcomercio
       AND d.anio = ${anioActual}
       AND d.nummes = m.nummes
      WHERE c.status = 51
      GROUP BY m.nummes, m.mes
    `;
      const orden = "m.nummes";
      const endpoint =
        op.conexion +
        `/api/comun/seleccionaregistros?campos=${encodeURIComponent(campos)}&nomtab=${encodeURIComponent(tabla)}&orden=${orden}`;

      const response = await Axios.get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
          "Content-Type": "application/json",
        },
      });

      // 👇 Normaliza los valores numéricos y guarda en state
      const data = (response.data || []).map((r) => ({
        nummes: Number(r.nummes),
        mes: r.mes,
        declararon: Number(r.declararon),
        faltaron: Number(r.faltaron),
      }));

      setDataComercios(data);
    } catch (error) {
      console.error("Error buscarComerciosDeclarados:", error);
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        mensaje: "",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
    }
  };
  const buscarGraficaPeriodo = async () => {
    try {
      const campos =
        parseInt(espiritu) === 1
          ? `
       m.nummes,
    m.mes,
    COALESCE(SUM(CASE WHEN d.tipo = 3 THEN 1 ELSE 0 END), 0) AS "primera",
    COALESCE(SUM(CASE WHEN d.tipo = 4 THEN 1 ELSE 0 END), 0) AS "segunda"`
          : `m.nummes,
    m.mes,
	SUM(d.tipo) as primera`;
      const tabla = `(
    SELECT 1 AS nummes, 'Enero' AS mes UNION ALL
    SELECT 2, 'Febrero' UNION ALL
    SELECT 3, 'Marzo' UNION ALL
    SELECT 4, 'Abril' UNION ALL
    SELECT 5, 'Mayo' UNION ALL
    SELECT 6, 'Junio' UNION ALL
    SELECT 7, 'Julio' UNION ALL
    SELECT 8, 'Agosto' UNION ALL
    SELECT 9, 'Septiembre' UNION ALL
    SELECT 10, 'Octubre' UNION ALL
    SELECT 11, 'Noviembre' UNION ALL
    SELECT 12, 'Diciembre'
) m
inner JOIN djurada d ON m.nummes = d.nummes AND d.anio = EXTRACT(YEAR FROM CURRENT_DATE)
inner JOIN comercio c ON c.idcomercio = d.idcomercio AND c.status = 51
GROUP BY m.nummes, m.mes`;
      const orden = "m.nummes";
      const endpoint =
        op.conexion +
        `/api/comun/seleccionaregistros?campos=${encodeURIComponent(campos)}&nomtab=${encodeURIComponent(tabla)}&orden=${orden}`;
      const response = await Axios.get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = (response.data || []).map((r) => ({
        nummes: Number(r.nummes),
        mes: r.mes,
        primera: Number(r.primera),
        segunda: Number(r.segunda),
      }));
      setGraficaPeriodo(data);
    } catch (error) {
      setMensaje({
        mostrar: true,
        titulo: "ERROR",
        mensaje: "",
        texto: error.response?.data?.message || error.message,
        icono: "error",
      });
    }
  };
  // ========== useEffect HOOKS ==========
  useEffect(() => {
    const obj = JSON.parse(localStorage.getItem("post"));
    if (obj) {
      setIdUsuario(obj.idusuario);
    }
    seleccionarConfiguracion();
    seleccionarFactorCambio();
    buscarComerciosDeclarados();
    buscarGraficaPeriodo();
  }, []);

  useEffect(() => {
    if (filtroTipo === "fecha" && (!fechaDesde || !fechaHasta)) {
      return;
    }
    fetchData();
  }, [filtroTipo, mesPeriodo, anioPeriodo, fechaDesde, fechaHasta, espiritu]);
  // ========== RENDERIZADO DE GRÁFICOS ==========
  const chartData = declaracionesData.declaracionesPorDia.map((item) => {
    let barColor = COLORS.primary;
    if (declaracionesData.mostrarQuincenas) {
      if (item.quincena === "1ra Quincena") {
        barColor = COLORS.quincena1;
      } else if (item.quincena === "2da Quincena") {
        barColor = COLORS.quincena2;
      }
    }
    let nombre = declaracionesData.mostrarQuincenas
      ? `${item.fecha} (${item.quincena || "N/A"})`
      : `${item.fecha}`;
    return {
      nombre: nombre,
      fecha: item.fecha,
      dia: item.dia,
      quincena: item.quincena,
      cantidad: item.cantidad_declaraciones,
      monto: item.monto_total_dia / 1000000,
      monto_original: item.monto_total_dia,
      barColor: barColor,
    };
  });

  const stackedChartData = () => {
    if (!declaracionesData.mostrarQuincenas) return [];
    const groupedByFecha = {};
    declaracionesData.declaracionesPorDia.forEach((item) => {
      if (!groupedByFecha[item.fecha]) {
        groupedByFecha[item.fecha] = {
          fecha: item.fecha,
          nombre: `${item.fecha}`,
          primeraQuincena: 0,
          segundaQuincena: 0,
          monto: 0,
        };
      }
      if (item.quincena === "1ra Quincena") {
        groupedByFecha[item.fecha].primeraQuincena +=
          item.cantidad_declaraciones;
      } else if (item.quincena === "2da Quincena") {
        groupedByFecha[item.fecha].segundaQuincena +=
          item.cantidad_declaraciones;
      }
      groupedByFecha[item.fecha].monto += item.monto_total_dia / 1000000;
    });
    return Object.values(groupedByFecha);
  };

  const stackedData = stackedChartData();

  const getTendenciaQuincenas = () => {
    if (!declaracionesData.mostrarQuincenas) return null;
    if (declaracionesData.distributionData.length === 2) {
      const primera = declaracionesData.distributionData[0].value;
      const segunda = declaracionesData.distributionData[1].value;
      if (primera === 0 && segunda === 0) return null;
      if (segunda > primera) {
        const incremento = (((segunda - primera) / primera) * 100).toFixed(1);
        return { texto: `↑ ${incremento}% más en 2da quincena`, tipo: "up" };
      } else if (primera > segunda) {
        const decremento = (((primera - segunda) / primera) * 100).toFixed(1);
        return { texto: `↓ ${decremento}% más en 1ra quincena`, tipo: "down" };
      }
      return { texto: "= Distribución equilibrada", tipo: "equal" };
    }
    return null;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const chartItem = chartData.find((item) => item.nombre === label);
      return (
        <div
          className="card shadow-sm border-0"
          style={{ backgroundColor: "white", minWidth: "250px" }}
        >
          <div className="card-body p-3">
            <h6 className="card-subtitle mb-2 text-muted">{label}</h6>
            {declaracionesData.mostrarQuincenas &&
              chartItem &&
              chartItem.quincena && (
                <div className="mb-2 pb-2 border-bottom">
                  <small className="text-muted">Quincena:</small>
                  <strong className="ms-2">
                    {chartItem.quincena === "1ra Quincena"
                      ? "🔵 1ra Quincena"
                      : "🟢 2da Quincena"}
                  </strong>
                </div>
              )}
            {payload.map((item, index) => (
              <div key={index} className="d-flex align-items-center gap-2 mt-1">
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: item.color,
                  }}
                />
                <small>
                  {item.name}:{" "}
                  <strong>
                    {item.name === "Monto (Bs.)"
                      ? formatCurrency(item.value * 1000000)
                      : formatNumber(item.value)}
                  </strong>
                </small>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // ========== HANDLERS ==========
  const handleChangePage = (newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    setPage(0);
  };
  const handleFiltroTipoChange = (tipo) => {
    setFiltroTipo(tipo);
    setPage(0);
  };

  const filteredContribuyentes = declaracionesData.todosContribuyentes.filter(
    (item) =>
      item.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.rif?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.reprelegal?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredContribuyentes.length / rowsPerPage),
  );

  if (loading) {
    return <Dimmer active inverted />;
  }

  const tendencia = getTendenciaQuincenas();

  const metricCards = [
    {
      title: "TOTAL DECLARACIONES",
      value: formatNumber(declaracionesData.resumen?.total_declaraciones),
      gradient: GRADIENTS.blue,
      icon: "fas fa-file-alt",
      subtitle: "Declaraciones registradas",
    },
    {
      title: "CONTRIBUYENTES",
      value: formatNumber(declaracionesData.resumen?.total_contribuyentes),
      gradient: GRADIENTS.green,
      icon: "fas fa-building",
      subtitle: "Contribuyentes activos",
    },
    {
      title: "TOTAL DECLARADO",
      value: `Bs. ${formatNumber(declaracionesData.resumen?.gran_total_declarado)}`,
      gradient: GRADIENTS.red,
      icon: "fas fa-chart-line",
      subtitle: "Monto total en Bs.",
    },
    {
      title: "TOTAL PAGADO",
      value: `Bs. ${formatNumber(
        declaracionesData.resumen?.promedio_por_declaracion,
      )}`,
      gradient: GRADIENTS.purple,
      icon: "fas fa-calculator",
      subtitle: "Por declaración",
    },
  ];

  const CardsModule = ({ cards }) => {
    return (
      <div className="row mb-4 mx-auto col-md-12">
        {cards.map((card, index) => (
          <div key={index} className="col-sm-6 col-lg-3">
            <div
              className="card h-100 border-0 overflow-hidden position-relative"
              style={{
                background: card.gradient,
                borderRadius: "20px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 10px 30px rgba(0,0,0,0.15)";
              }}
            >
              {/* Círculos decorativos */}
              <div
                style={{
                  position: "absolute",
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.08)",
                  pointerEvents: "none",
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  bottom: -30,
                  left: -30,
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                  pointerEvents: "none",
                }}
              ></div>

              <div
                className="card-body p-3 p-md-4 position-relative"
                style={{ zIndex: 1 }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <small
                      className="text-uppercase fw-bold"
                      style={{
                        letterSpacing: "2px",
                        fontSize: "0.7rem",
                        opacity: 0.85,
                        color: "white",
                      }}
                    >
                      {card.title}
                    </small>
                    <h3
                      className="mb-1 mt-2 fw-bold"
                      style={{
                        color: "white",
                        fontSize: "clamp(1.5rem, 4vw, 1.6rem)",
                      }}
                    >
                      {card.value}
                    </h3>
                    {card.subtitle && (
                      <small
                        style={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: "0.7rem",
                        }}
                      >
                        {card.subtitle}
                      </small>
                    )}
                  </div>
                  <div className="ms-3">
                    <i
                      className={card.icon}
                      style={{
                        color: "rgba(255,255,255,0.2)",
                        fontSize: "3rem",
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                      }}
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };
  const handleSort = (key) => {
    let direction = "asc";
    // Si la columna clickeada ya es la que se está ordenando, invertimos el orden
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey)
      return <i className="fas fa-sort ms-1 opacity-50"></i>;
    if (sortConfig.direction === "asc")
      return <i className="fas fa-sort-up ms-1"></i>;
    return <i className="fas fa-sort-down ms-1"></i>;
  };
  return (
    <div className="min-vh-100 bg-light">
      {/* HEADER */}
      <div
        className="position-relative mb-4"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "0 0 30px 30px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
        }}
      >
        <div className="container-fluid py-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-4">
            <div className="col-md-4">
              <h1 className="text-white fw-bold mb-2">
                <i className="fas fa-chart-pie me-3"></i>
                Análisis de Declaraciones
              </h1>
              <p className="text-white-50 mb-0">{getSubtituloFiltro()}</p>
            </div>
            <div className="bg-light rounded py-2  px-4">
              <div className="d-flex  align-items-start  flex-wrap gap-4">
                <div className="d-flex flex-column gap-0">
                  <small className="  fw-bold text-uppercase mb-1">
                    Filtrar por
                  </small>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input my-auto"
                        type="radio"
                        name="filtroTipo"
                        id="filtroPeriodo"
                        value="periodo"
                        checked={filtroTipo === "periodo"}
                        onChange={() => handleFiltroTipoChange("periodo")}
                      />
                      <label
                        className="form-check-label fw-medium"
                        htmlFor="filtroPeriodo"
                      >
                        <CalendarDays size={14} className="me-1 text-primary" />
                        Periodo
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input my-auto"
                        type="radio"
                        name="filtroTipo"
                        id="filtroFecha"
                        value="fecha"
                        checked={filtroTipo === "fecha"}
                        onChange={() => handleFiltroTipoChange("fecha")}
                      />
                      <label
                        className="form-check-label fw-medium"
                        htmlFor="filtroFecha"
                      >
                        <Calendar size={14} className="me-1 text-success" />
                        Fecha
                      </label>
                    </div>
                  </div>
                </div>

                <div
                  className="d-none d-md-block"
                  style={{
                    width: 1,
                    background: "#DEE2E6",
                    alignSelf: "stretch",
                    minHeight: 50,
                  }}
                ></div>

                {filtroTipo === "periodo" ? (
                  <div className="d-flex align-items-end gap-2 flex-wrap">
                    <div>
                      <label
                        htmlFor="selectMes"
                        className="form-label mb-1 small text-muted fw-semibold"
                      >
                        Mes
                      </label>
                      <select
                        id="selectMes"
                        className="form-select form-select-sm"
                        value={mesPeriodo}
                        onChange={(e) => setMesPeriodo(e.target.value)}
                        style={{ minWidth: 160 }}
                      >
                        {MESES.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="selectAnio"
                        className="form-label mb-1 small text-muted fw-semibold"
                      >
                        Año
                      </label>
                      <select
                        id="selectAnio"
                        className="form-select form-select-sm"
                        value={anioPeriodo}
                        onChange={(e) => setAnioPeriodo(e.target.value)}
                        style={{ minWidth: 100 }}
                      >
                        {ANIOS.map((a) => (
                          <option key={a.value} value={a.value}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex align-items-end gap-2 flex-wrap">
                    <div>
                      <label
                        htmlFor="inputFechaDesde"
                        className="form-label mb-1 small text-muted fw-semibold"
                      >
                        Desde
                      </label>
                      <input
                        id="inputFechaDesde"
                        type="date"
                        className="form-control form-control-sm"
                        value={fechaDesde}
                        onChange={(e) => setFechaDesde(e.target.value)}
                        style={{ minWidth: 160 }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="inputFechaHasta"
                        className="form-label mb-1 small text-muted fw-semibold"
                      >
                        Hasta
                      </label>
                      <input
                        id="inputFechaHasta"
                        type="date"
                        className="form-control form-control-sm"
                        value={fechaHasta}
                        onChange={(e) => setFechaHasta(e.target.value)}
                        style={{ minWidth: 160 }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL */}
      <div className="col-md-12 mx-auto row mt-0 py-0 px-0">
        {/* Métricas */}

        <CardsModule cards={metricCards} />

        {/* Gráfico de Actividad por Día */}
        <div className="mb-4">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-0 pt-4 px-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h5 className="mb-0 fw-semibold">{getTituloGrafico()}</h5>
                <div className="d-flex gap-2">
                  <div className="btn-group btn-group-sm">
                    {espiritu !== 0 ? (
                      <button
                        onClick={() => setChartType("bar")}
                        className={`btn ${chartType === "bar" ? "btn-primary" : "btn-outline-secondary"}`}
                      >
                        📊 Barras
                      </button>
                    ) : (
                      ""
                    )}
                    {espiritu !== 0 ? (
                      <button
                        onClick={() => setChartType("line")}
                        className={`btn ${chartType === "line" ? "btn-primary" : "btn-outline-secondary"}`}
                      >
                        📈 Líneas
                      </button>
                    ) : (
                      ""
                    )}
                    {declaracionesData.mostrarQuincenas && (
                      <button
                        onClick={() => setChartType("stacked")}
                        className={`btn ${chartType === "stacked" ? "btn-primary" : "btn-outline-secondary"}`}
                      >
                        📊 Apiladas
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      setExpandedChart(expandedChart === "bar" ? null : "bar")
                    }
                    className="btn btn-sm btn-outline-secondary"
                  >
                    {expandedChart === "bar" ? (
                      <Minimize2 size={16} />
                    ) : (
                      <Maximize2 size={16} />
                    )}
                  </button>
                </div>
              </div>
              {declaracionesData.mostrarQuincenas && (
                <div className="mt-2 d-flex gap-3">
                  <small className="text-muted">🔵 Azul: 1ra Quincena</small>
                  <small className="text-muted">🟢 Verde: 2da Quincena</small>
                  <small className="text-muted">
                    * Cada barra muestra la fecha y qué quincena se declaró
                  </small>
                </div>
              )}
            </div>
            <div className="card-body">
              <ResponsiveContainer
                width="100%"
                height={expandedChart === "bar" ? 550 : 400}
              >
                {chartType === "bar" && (
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="nombre"
                      stroke="#6B7280"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                    />
                    <YAxis
                      yAxisId="left"
                      stroke="#3B82F6"
                      label={{
                        value: "Cantidad",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="cantidad"
                      fill={COLORS.primary}
                      name="Cantidad Declaraciones"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                )}
                {chartType === "line" && (
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="nombre"
                      stroke="#6B7280"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                    />
                    <YAxis yAxisId="left" stroke="#3B82F6" />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#10B981"
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="cantidad"
                      stroke={COLORS.primary}
                      name="Cantidad Declaraciones"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                )}
                {chartType === "stacked" &&
                  declaracionesData.mostrarQuincenas && (
                    <BarChart data={stackedData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="nombre"
                        stroke="#6B7280"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        interval={0}
                      />
                      <YAxis
                        yAxisId="left"
                        stroke="#3B82F6"
                        label={{
                          value: "Cantidad",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="primeraQuincena"
                        stackId="a"
                        fill={COLORS.quincena1}
                        name="1ra Quincena"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="segundaQuincena"
                        stackId="a"
                        fill={COLORS.quincena2}
                        name="2da Quincena"
                      />
                    </BarChart>
                  )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top 10 Días y Distribución */}
        <div className="row col-md-4 mb-4">
          <div
            className={
              declaracionesData.mostrarQuincenas ? "col-md-12" : "col-md-12"
            }
          >
            {/* Top 10 Días - Modificar el header y agregar el select */}

            <div className="card shadow-sm border-0 h-100">
              <div className="card-header azulGradiant bg-white border-0 pt-4 px-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0 text-light fw-semibold">
                    Top Días con Más Declaraciones
                  </h3>
                  <div className="d-flex align-items-center gap-2">
                    <label className="text-light small mb-0">Mostrar:</label>
                    <select
                      className="form-select form-select-sm bg-light border-0 rounded-pill"
                      value={topDiasLimit}
                      onChange={(e) => setTopDiasLimit(Number(e.target.value))}
                      style={{ width: "auto", cursor: "pointer" }}
                    >
                      <option value={10}>10 días</option>
                      <option value={15}>15 días</option>
                      <option value={20}>20 días</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <div
                    style={{
                      maxHeight: 300,
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                    className="border-table"
                  >
                    <table
                      className="table table-sm border-table table-hover mb-0"
                      style={{ position: "relative" }}
                    >
                      <thead
                        className="table-light azulGradiant text-light fw-bold text-center text-uppercase"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <tr className="fw-bold">
                          <th>Fecha</th>
                          {declaracionesData.mostrarQuincenas && (
                            <th>Quincena</th>
                          )}
                          <th className="text-end">Cantidad</th>
                          <th className="text-end">Total Declarado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getTopDias().map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.fecha}</td>
                            {declaracionesData.mostrarQuincenas && (
                              <td>
                                <button
                                  className={`btn btn-sm rounded-pill ${item.quincena === "1ra Quincena" ? "btn-primary" : "btn-success"}`}
                                >
                                  {item.quincena || "N/A"}
                                </button>
                              </td>
                            )}
                            <td className="text-end">
                              <span className="btn btn-sm rounded-pill btn-primary bg-opacity-10 px-2 py-1">
                                {formatNumber(item.cantidad_declaraciones)}
                              </span>
                            </td>
                            <td className="text-end">
                              Bs. {formatNumber(item.monto_total_dia)}
                            </td>
                          </tr>
                        ))}
                        {getTopDias().length === 0 && (
                          <tr>
                            <td
                              colSpan={
                                declaracionesData.mostrarQuincenas ? 4 : 3
                              }
                              className="text-center text-muted py-4"
                            >
                              No hay datos disponibles
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {declaracionesData.mostrarQuincenas && (
            <div className="col-md-12">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-header bg-white border-0 pt-4 px-4">
                  <h5 className="mb-0 fw-semibold">
                    Distribución por Quincenas
                  </h5>
                </div>
                <div className="card-body">
                  {declaracionesData.distributionData.some(
                    (d) => d.value > 0,
                  ) ? (
                    <>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={declaracionesData.distributionData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={5}
                            dataKey="value"
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {declaracionesData.distributionData.map(
                              (entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry.color}
                                />
                              ),
                            )}
                          </Pie>
                          <Tooltip formatter={(value) => formatNumber(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                      {tendencia && (
                        <div className="text-center mt-3">
                          {tendencia.tipo === "up" && (
                            <span className="badge bg-success">
                              <TrendingUp size={12} className="me-1" />
                              {tendencia.texto}
                            </span>
                          )}
                          {tendencia.tipo === "down" && (
                            <span className="badge bg-warning">
                              <TrendingDown size={12} className="me-1" />
                              {tendencia.texto}
                            </span>
                          )}
                          {tendencia.tipo === "equal" && (
                            <span className="badge bg-info">
                              {tendencia.texto}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: 250 }}
                    >
                      <p className="text-muted">Sin datos para mostrar</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Top 10 Contribuyentes */}
        <div className="mb-4 col-md-8 px-0 mx-auto">
          <div className="card shadow-sm border-0">
            <div className="card-header azulGradiant text-light border-0 pt-4 px-4">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0 text-light fw-semibold">
                  Top Contribuyentes que Más Declararon
                </h3>

                <div className="d-flex align-items-center gap-2">
                  <label className="text-light small mb-0">Mostrar:</label>
                  <select
                    className="form-select form-select-sm bg-light border-0 rounded-pill"
                    value={topContribuyentesLimit}
                    onChange={(e) =>
                      setTopContribuyentesLimit(Number(e.target.value))
                    }
                    style={{ width: "auto", cursor: "pointer" }}
                  >
                    <option value={10}>10 contribuyentes</option>
                    <option value={15}>15 contribuyentes</option>
                    <option value={20}>20 contribuyentes</option>
                    <option value={25}>25 contribuyentes</option>
                    <option value={50}>50 contribuyentes</option>
                    <option value={100}>100 contribuyentes</option>
                  </select>
                  <button
                    onClick={() => {
                      setMostrarDetallado(true);
                    }}
                    className="btn btn-sm mx-1 btn-info rounded-circle"
                  >
                    <i className="fas fa-file" />
                  </button>
                  <button
                    onClick={() => {
                      setMostrarContriDecla(true);
                      setTodosContri(false);
                    }}
                    className="btn btn-sm mx-1 btn-success rounded-circle"
                  >
                    <i className="fas fa-print"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body p-0">
              <div
                style={{
                  maxHeight: 400,
                  overflowY: "auto",
                  overflowX: "auto",
                }}
                className="border-table"
              >
                <table
                  className="table table-sm border-table table-hover mb-0"
                  style={{ position: "relative" }}
                >
                  <thead
                    className="table-light azulGradiant text-light fw-bold text-center text-uppercase"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th className="text-center">#</th>
                      <th className="text-center">RIF</th>
                      <th className="text-center">Nombre</th>
                      <th className="text-center">Cantidad</th>
                      <th className="text-center">Declarado</th>
                      <th className="text-center">Monto a Pagar Bs:</th>
                      <th className="text-center">Monto a Pagar $:</th>
                      {/* <th className="text-center">Monto a Pagar €:</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {/* CAMBIO IMPORTANTE: Aquí se ordena y se limita */}
                    {[...declaracionesData.todosContribuyentes]
                      .sort((a, b) => b.total_declarado - a.total_declarado)
                      .slice(0, topContribuyentesLimit)
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td className="text-center">
                            <span
                              style={{ height: 32, width: 32 }}
                              className={`btn-sm btn text-light ${idx < 3 ? "btn-warning" : "btn-secondary"} rounded-circle d-flex align-items-center justify-content-center mx-auto`}
                            >
                              {idx + 1}
                            </span>
                          </td>
                          <td className="text-center" style={{ width: "auto" }}>
                            {item.rif?.trim()}
                          </td>
                          <td>
                            <span
                              className="d-inline-block text-truncate"
                              style={{ width: "auto" }}
                              title={item.nombre}
                            >
                              {item.nombre?.substring(0, 50)}
                              {(item.nombre?.length || 0) > 50 ? "..." : ""}
                            </span>
                          </td>
                          <td className="text-center" style={{ width: "auto" }}>
                            {formatMoneda(
                              (item.cantidad_declaraciones || 0)
                                .toString()
                                .replace(".", ","),
                              ",",
                              ".",
                              2,
                            )}
                          </td>
                          <td className="text-end" style={{ width: "auto" }}>
                            {formatMoneda(
                              (item.total_declarado || 0)
                                .toString()
                                .replace(".", ","),
                              ",",
                              ".",
                              2,
                            )}
                          </td>
                          <td className="text-end" style={{ width: "auto" }}>
                            {formatMoneda(
                              (item.montoPagarBs || 0)
                                .toString()
                                .replace(".", ","),
                              ",",
                              ".",
                              2,
                            )}
                          </td>
                          <td className="text-end" style={{ width: "auto" }}>
                            {formatMoneda(
                              (item.montoPagardolar || 0)
                                .toString()
                                .replace(".", ","),
                              ",",
                              ".",
                              2,
                            )}
                          </td>
                          {/* <td className="text-end" style={{ width: "auto" }}>
                            {formatNumber(item.total_petro)}
                          </td> */}
                        </tr>
                      ))}
                    {declaracionesData.todosContribuyentes.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          No hay datos disponibles
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla completa de contribuyentes */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0 py-2 rounded px-0 ">
            <div className="d-flex py-2 justify-content-between align-items-center  azulGradiant col-md-12 flex-wrap gap-3">
              <h3 className="mb-0 text-light fw-semibold">
                Todos los Contribuyentes
              </h3>

              <div className="position-relative ml-auto">
                <Search
                  size={18}
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                />

                <input
                  type="text"
                  placeholder="Buscar por nombre, RIF o representante..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control ps-5 rounded-pill border-0 bg-light"
                  style={{ width: "300px" }}
                />
              </div>
              <button
                onClick={() => {
                  setMostrarContriDecla(true);
                  setTodosContri(true);
                }}
                className="btn btn-sm mx-1 btn-success rounded-circle"
              >
                <i className="fas fa-print"></i>
              </button>
            </div>
          </div>
          {/* <div className="px-4 pt-2">
            <ul className="nav mb-2" id="ex-with-icons" role="tablist">
              <li className="nav-item">
                <button
                  onClick={() => handleTabChange(0)}
                  className={`nav-link  rounded bg-blue-100 text-light ${tabValue === 0 ? "active" : ""}`}
                >
                  Todos
                </button>
              </li>
              <li className="nav-item">
                <button
                  onClick={() => handleTabChange(1)}
                  className={`nav-link  rounded bg-blue-200 text-light ${tabValue === 1 ? "active" : ""}`}
                >
                  Mayor Declarados
                </button>
              </li>
              <li className="nav-item">
                <button
                  onClick={() => handleTabChange(2)}
                  className={`nav-link  rounded bg-blue-300 text-light${tabValue === 2 ? "active" : ""}`}
                >
                  Menor Declarados
                </button>
              </li>
            </ul>
          </div> */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-hover border-table mb-0">
                <thead className="table-light azulGradiant text-light fw-bold text-center text-uppercase">
                  <tr>
                    {/* Agregamos style={{ cursor: "pointer" }} y onClick a cada th */}
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("idcomercio")}
                    >
                      Codigo {getSortIcon("idcomercio")}
                    </th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("rif")}
                    >
                      RIF {getSortIcon("rif")}
                    </th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("nombre")}
                    >
                      Nombre {getSortIcon("nombre")}
                    </th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("reprelegal")}
                    >
                      Representante {getSortIcon("reprelegal")}
                    </th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("cantidad_declaraciones")}
                    >
                      # Declaraciones {getSortIcon("cantidad_declaraciones")}
                    </th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("total_declarado")}
                    >
                      Total Declarado {getSortIcon("total_declarado")}
                    </th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("montoPagarBs")}
                    >
                      Monto a Pagar Bs: {getSortIcon("montoPagarBs")}
                    </th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("montoPagardolar")}
                    >
                      Monto a Pagar $: {getSortIcon("montoPagardolar")}
                    </th>
                    <th className="text-center">
                      <i className="fas fa-id-card"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let datos = [...filteredContribuyentes];

                    // --- NUEVA LÓGICA DE ORDENAMIENTO ---
                    if (sortConfig.key) {
                      datos.sort((a, b) => {
                        let valA = a[sortConfig.key];
                        let valB = b[sortConfig.key];

                        // Manejar valores nulos o indefinidos
                        if (valA == null) valA = "";
                        if (valB == null) valB = "";

                        // Ordenamiento numérico
                        if (
                          typeof valA === "number" &&
                          typeof valB === "number"
                        ) {
                          return sortConfig.direction === "asc"
                            ? valA - valB
                            : valB - valA;
                        }

                        // Ordenamiento de texto (convirtiendo a minúsculas para que sea case-insensitive)
                        valA = valA.toString().toLowerCase();
                        valB = valB.toString().toLowerCase();

                        if (valA < valB)
                          return sortConfig.direction === "asc" ? -1 : 1;
                        if (valA > valB)
                          return sortConfig.direction === "asc" ? 1 : -1;
                        return 0;
                      });
                    }
                    // Si no hay columna seleccionada, usamos tu lógica anterior de las pestañas (tabs)
                    else if (tabValue === 1) {
                      datos.sort(
                        (a, b) => b.total_declarado - a.total_declarado,
                      );
                    } else if (tabValue === 2) {
                      datos.sort(
                        (a, b) => a.total_declarado - b.total_declarado,
                      );
                    }

                    // Paginación (permanece igual)
                    const paginated = datos.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    );

                    return paginated.length > 0 ? (
                      paginated.map((item) => (
                        <tr key={item.idcomercio}>
                          <td>{item.idcomercio}</td>
                          <td className="text-center" style={{ width: "auto" }}>
                            <code>{item.rif?.trim()}</code>
                          </td>
                          <td>
                            <span
                              className="d-inline-block text-truncate"
                              style={{ width: "auto" }}
                              title={item.nombre}
                            >
                              {item.nombre?.substring(0, 80)}
                              {(item.nombre?.length || 0) > 80 ? "..." : ""}
                            </span>
                          </td>
                          <td className="text-center" style={{ width: "auto" }}>
                            {item.reprelegal}
                          </td>
                          <td className="text-center" style={{ width: "auto" }}>
                            <span className="btn btn-secondary bg-opacity-10 text-light mx-auto btn-sm">
                              {formatMoneda(
                                (item.cantidad_declaraciones || 0)
                                  .toString()
                                  .replace(".", ","),
                                ",",
                                ".",
                                2,
                              )}
                            </span>
                          </td>
                          <td className="text-end" style={{ width: "auto" }}>
                            {formatMoneda(
                              (item.total_declarado || 0)
                                .toString()
                                .replace(".", ","),
                              ",",
                              ".",
                              2,
                            )}
                          </td>
                          <td className="text-end" style={{ width: "auto" }}>
                            {formatMoneda(
                              (item.montoPagarBs || 0)
                                .toString()
                                .replace(".", ","),
                              ",",
                              ".",
                              2,
                            )}
                          </td>
                          <td className="text-end" style={{ width: "auto" }}>
                            {formatMoneda(
                              (item.montoPagardolar || 0)
                                .toString()
                                .replace(".", ","),
                              ",",
                              ".",
                              2,
                            )}
                          </td>
                          <td className="text-center" style={{ width: "auto" }}>
                            <button
                              type="button"
                              style={{ width: "auto" }}
                              onClick={realizarDeclaracion(item.idcomercio)}
                              className="btn btn-sm btn-info rounded-circle"
                            >
                              <i className="fas fa-id-card"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        {/* Corregido a 9 para que abarque toda la tabla */}
                        <td colSpan={9} className="text-center text-muted py-4">
                          No se encontraron resultados
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-white">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted">Filas por página:</small>
                <select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  className="form-select form-select-sm w-auto"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <small className="text-muted ms-2">
                  ({filteredContribuyentes.length} registros en total)
                </small>
              </div>
              <div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${page === 0 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handleChangePage(page - 1)}
                      >
                        Anterior
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">
                        Página {page + 1} de {totalPages}
                      </span>
                    </li>
                    <li
                      className={`page-item ${page >= totalPages - 1 ? "disabled" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handleChangePage(page + 1)}
                      >
                        Siguiente
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header azulGradiant text-light border-0 pt-4 px-4">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0 text-light fw-semibold">
                Comercios que Declararon vs No Declararon por Mes ({anioActual})
              </h3>
            </div>
          </div>
          <div className="card-body border-table">
            {/* 1. Aumentamos la altura de 320 a 380 para dar más respiro */}
            <ResponsiveContainer width="100%" height={380}>
              {/* 2. Añadimos un margen superior para que los labels tengan espacio */}
              <BarChart
                data={dataComercios}
                responsive
                margin={{ top: 25, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="mes" stroke="#6B7280" />

                {/* 3. Ampliamos el dominio del Eje Y automáticamente un 15% por encima del valor máximo */}
                <YAxis
                  stroke="#6B7280"
                  domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                />

                <Tooltip
                  formatter={(value, name) => [formatNumber(value), name]}
                  labelFormatter={(label, payload) => label}
                />
                <Legend />

                <Bar
                  dataKey="declararon"
                  name="Declararon"
                  fill="#10B981"
                  radius={[4, 4, 0, 0]}
                >
                  {/* Opcional: estilizamos el LabelList con un texto un poco más compacto */}
                  <LabelList
                    dataKey="declararon"
                    position="top"
                    style={{ fontSize: "12px", fill: "#4B5563" }}
                  />
                </Bar>

                <Bar
                  dataKey="faltaron"
                  name="Faltaron por Declarar"
                  fill="#EF4444"
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    dataKey="faltaron"
                    position="top"
                    style={{ fontSize: "12px", fill: "#4B5563" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header azulGradiant text-light border-0 pt-4 px-4">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0 text-light fw-semibold">
                Comercios Declarados x Mes ({anioActual})
              </h3>
            </div>
          </div>
          <div className="card-body border-table">
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={dataGraficaPeriodo}
                responsive
                margin={{ top: 25, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="mes" stroke="#6B7280" />

                <YAxis
                  stroke="#6B7280"
                  domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]}
                />

                <Tooltip
                  formatter={(value, name) => [formatNumber(value), name]}
                  labelFormatter={(label, payload) => label}
                />
                <Legend />

                <Bar
                  dataKey="primera"
                  name="1 Quincina"
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    dataKey="primera"
                    position="top"
                    style={{ fontSize: "12px", fill: "#4B5563" }}
                  />
                </Bar>
                {parseInt(espiritu) === 1 && (
                  <Bar
                    dataKey="segunda"
                    name="2 Quincina"
                    fill="#10B981"
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList
                      dataKey="segunda"
                      position="top"
                      style={{ fontSize: "12px", fill: "#4B5563" }}
                    />
                  </Bar>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* <DeclararUC
          show={mostrar5}
          onHideCancela={cerrarModalActualizar}
          onHideAcepta={validarActuComercio}
          comercio={comercio}
          representante={persona}
          tabdeuda={deuda}
          tabpago={historico}
          tabactixcomer={actividadxCome}
          deshabilitado={deshabilitado || undefined}
          declaweb={configCome ? configCome.declaweb.toString() : "false"}
          config={configCome}
          idusuario={idUsuario}
          operacion={operacion}
          totdeuda={totDeuda}
          factorCambio={factorCambio}
        /> */}
        {/* <DeclararUCRet
          show={mostrar6}
          onHideCancela={cerrarModalActualizar}
          onHideAcepta={validarActuComercio}
          comercio={comercio}
          representante={persona}
          tabdeuda={deuda}
          tabpago={historico}
          tabactixcomer={actividadxCome}
          deshabilitado={deshabilitado || undefined}
          declaweb={configCome ? configCome.declaweb.toString() : "false"}
          config={configCome}
          idusuario={idUsuario}
          operacion={operacion}
          totdeuda={totDeuda}
          factorCambio={factorCambio}
        /> */}
        <ActualizarCamposComercio
          show={mostrar2}
          onHideCancela={cerrarModalActualizar}
          onHideAceptaActuCampo={validarActuCamposComercio}
          comercio={comercio}
          representante={persona}
          tabactixcomer={actividadxCome}
          declaweb={configCome ? configCome.declaweb.toString() : "false"}
          operacion={operacion}
        />
        <Mensaje
          mensaje={mensaje}
          onHide={() =>
            setMensaje({ mostrar: false, titulo: "", texto: "", icono: "" })
          }
        />
        <Dimmer active={activate} inverted />
        <Toasts
          show={toast.mostrar}
          titulo={toast.titulo}
          mensaje={toast.mensaje}
          onHideCancela={cerrarToast}
        />
        <ModalImprimirContriDecla
          show={mostrarContriDecla}
          onHide={() => setMostrarContriDecla(false)}
          anio={anioPeriodo}
          mes={mesPeriodo}
          desde={fechaDesde}
          hasta={fechaHasta}
          cantidad={topContribuyentesLimit}
          filtroTipo={filtroTipo}
          todos={todosContri}
        />
        <ActualizarEstadoCuenta
          show={mostrar5}
          onHideCancela={() => setMostrar5(false)}
          comercio={comercio}
          representante={persona}
          tabdeuda={deuda}
          tabpago={historico}
          deshabilitado={deshabilitado ? deshabilitado : undefined}
          espiritu={espiritu}
          factorCambio={factorCambio?.valor}
          actividad={actividad}
          actividadcome={actividad}
        />
        <ModalDetalladoContribuyentes
          show={mostrarDetallado}
          onHide={() => setMostrarDetallado(false)}
          anio={anioPeriodo}
          limite={topContribuyentesLimit}
        />
      </div>

      <style jsx>{`
        .bg-gradient-text {
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .hover-shadow {
          transition: all 0.3s ease;
        }
        .hover-shadow:hover {
          transform: translateY(-4px);
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default AnalisisModuloAE;
